/**
 * <ls-newsletter-form> — swaps the browser's native validation tooltip for the
 * design system's inline error message on the Newsletter section's email field,
 * WITHOUT disabling native validation.
 *
 * Same shape as <ls-contact-form> (assets/ls-contact.js): the browser stays the
 * source of truth for blocking submits — a `required`/invalid field can never
 * POST, even if this script fails to run. We only listen for the `invalid`
 * event the browser fires, suppress its default tooltip, and reveal our own
 * styled hint (`.ls-newsletter__error`) instead.
 *
 * Custom element (not an inline <script>) so it re-initialises when Shopify's
 * theme editor swaps in fresh markup via AJAX.
 */
if (!customElements.get('ls-newsletter-form')) {
  class LsNewsletterForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.handleInvalid = this.handleInvalid.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleInput = this.handleInput.bind(this);

      // `invalid` does not bubble — capture it at the form to catch the field.
      this.form.addEventListener('invalid', this.handleInvalid, true);
      this.form.addEventListener('submit', this.handleSubmit);
      this.form.addEventListener('input', this.handleInput);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('invalid', this.handleInvalid, true);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.form?.removeEventListener('input', this.handleInput);
    }

    // Fires per invalid field during a submit attempt. The browser still blocks
    // the submit; we just replace its tooltip with our inline message.
    handleInvalid(event) {
      const field = event.target;
      if (!field.matches || !field.matches('.ls-newsletter__input')) return;

      event.preventDefault(); // suppress the native tooltip
      this.showError(field, this.messageFor(field));
      field.focus();
    }

    // Only runs when the form is valid (the browser blocks it otherwise), so a
    // real submit means the field passed — clear any leftover error state.
    handleSubmit() {
      this.form
        .querySelectorAll('.ls-newsletter__input')
        .forEach((field) => this.clearError(field));
    }

    handleInput(event) {
      const field =
        event.target.closest && event.target.closest('.ls-newsletter__input');
      if (field && field.classList.contains('is-error') && field.checkValidity()) {
        this.clearError(field);
      }
    }

    messageFor(field) {
      const v = field.validity;
      const required = this.dataset.requiredMessage || 'This field is required';
      const invalid = this.dataset.invalidMessage || 'Please enter a valid email address';
      if (v.valueMissing) return required;
      if (v.typeMismatch && field.type === 'email') return invalid;
      return required;
    }

    showError(field, message) {
      field.classList.add('is-error');
      field.setAttribute('aria-invalid', 'true');
      const hint = this.hintFor(field);
      if (hint) {
        hint.textContent = message;
        hint.hidden = false;
      }
    }

    clearError(field) {
      field.classList.remove('is-error');
      field.removeAttribute('aria-invalid');
      const hint = this.hintFor(field);
      if (hint) hint.hidden = true;
    }

    hintFor(field) {
      return this.querySelector('[data-field-error]');
    }
  }

  customElements.define('ls-newsletter-form', LsNewsletterForm);
}
